import React, { useState } from 'react';
import { MdAdd, MdEdit, MdDelete, MdClose, MdSearch } from 'react-icons/md';

interface Order {
  id: string;
  type: '受注' | '発注';
  orderNumber: string;
  itemName: string;
  quantity: number;
  unitPrice: number;
  totalAmount: number;
  supplier: string;
  customer: string;
  orderDate: string;
  deliveryDate: string;
  status: '未処理' | '処理中' | '完了' | 'キャンセル';
  notes: string;
}

interface OrderForm {
  type: '受注' | '発注';
  itemName: string;
  quantity: string;
  unitPrice: string;
  supplier: string;
  customer: string;
  orderDate: string;
  deliveryDate: string;
  status: '未処理' | '処理中' | '完了' | 'キャンセル';
  notes: string;
}

// モックデータ
const mockOrders: Order[] = [
  {
    id: 'ORD001',
    type: '発注',
    orderNumber: 'PO-2025-001',
    itemName: 'スチール板材 5mm',
    quantity: 100,
    unitPrice: 15000,
    totalAmount: 1500000,
    supplier: '鋼材株式会社',
    customer: '',
    orderDate: '2025-01-15',
    deliveryDate: '2025-01-25',
    status: '処理中',
    notes: '定期発注分'
  },
  {
    id: 'ORD002',
    type: '受注',
    orderNumber: 'SO-2025-012',
    itemName: '完成品 モーターケース',
    quantity: 50,
    unitPrice: 8500,
    totalAmount: 425000,
    supplier: '',
    customer: 'ABC製造株式会社',
    orderDate: '2025-01-14',
    deliveryDate: '2025-02-01',
    status: '未処理',
    notes: '特急対応希望'
  },
  {
    id: 'ORD003',
    type: '発注',
    orderNumber: 'PO-2025-002',
    itemName: 'ボルト M8x50',
    quantity: 500,
    unitPrice: 80,
    totalAmount: 40000,
    supplier: 'ネジ専門商事',
    customer: '',
    orderDate: '2025-01-13',
    deliveryDate: '2025-01-20',
    status: '完了',
    notes: ''
  },
  {
    id: 'ORD004',
    type: '受注',
    orderNumber: 'SO-2025-013',
    itemName: '電動ドリル替刃',
    quantity: 30,
    unitPrice: 1200,
    totalAmount: 36000,
    supplier: '',
    customer: '工具販売株式会社',
    orderDate: '2025-01-12',
    deliveryDate: '2025-01-22',
    status: '処理中',
    notes: ''
  }
];

interface OrderManagementProps {
  onBack?: () => void;
}

export default function OrderManagement({ onBack }: OrderManagementProps) {
  const [filterType, setFilterType] = useState<'all' | '受注' | '発注'>('all');
  const [filterStatus, setFilterStatus] = useState<'all' | '未処理' | '処理中' | '完了' | 'キャンセル'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [orders, setOrders] = useState<Order[]>(mockOrders);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingOrder, setEditingOrder] = useState<Order | null>(null);
  const [formData, setFormData] = useState<OrderForm>({
    type: '受注',
    itemName: '',
    quantity: '',
    unitPrice: '',
    supplier: '',
    customer: '',
    orderDate: new Date().toISOString().split('T')[0],
    deliveryDate: '',
    status: '未処理',
    notes: ''
  });

  const filteredOrders = orders.filter(order => {
    if (filterType !== 'all' && order.type !== filterType) return false;
    if (filterStatus !== 'all' && order.status !== filterStatus) return false;
    if (searchTerm && !order.itemName.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !order.orderNumber.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  const getStatusClass = (status: string) => {
    switch (status) {
      case '未処理': return 'status-pending';
      case '処理中': return 'status-processing';
      case '完了': return 'status-completed';
      case 'キャンセル': return 'status-cancelled';
      default: return '';
    }
  };

  const getTypeClass = (type: string) => {
    return type === '受注' ? 'order-type-sales' : 'order-type-purchase';
  };

  const handleOpenModal = (order?: Order) => {
    if (order) {
      setEditingOrder(order);
      setFormData({
        type: order.type,
        itemName: order.itemName,
        quantity: String(order.quantity),
        unitPrice: String(order.unitPrice),
        supplier: order.supplier,
        customer: order.customer,
        orderDate: order.orderDate,
        deliveryDate: order.deliveryDate,
        status: order.status,
        notes: order.notes
      });
    } else {
      setEditingOrder(null);
      setFormData({
        type: '受注',
        itemName: '',
        quantity: '',
        unitPrice: '',
        supplier: '',
        customer: '',
        orderDate: new Date().toISOString().split('T')[0],
        deliveryDate: '',
        status: '未処理',
        notes: ''
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingOrder(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const quantity = parseInt(formData.quantity);
    const unitPrice = parseInt(formData.unitPrice);
    const totalAmount = quantity * unitPrice;
    
    if (editingOrder) {
      // 編集
      setOrders(orders.map(order => 
        order.id === editingOrder.id 
          ? {
              ...order,
              type: formData.type,
              itemName: formData.itemName,
              quantity,
              unitPrice,
              totalAmount,
              supplier: formData.supplier,
              customer: formData.customer,
              orderDate: formData.orderDate,
              deliveryDate: formData.deliveryDate,
              status: formData.status,
              notes: formData.notes
            }
          : order
      ));
    } else {
      // 新規作成
      const newOrder: Order = {
        id: `ORD${String(orders.length + 1).padStart(3, '0')}`,
        orderNumber: formData.type === '受注' ? `SO-2025-${String(orders.length + 1).padStart(3, '0')}` : `PO-2025-${String(orders.length + 1).padStart(3, '0')}`,
        type: formData.type,
        itemName: formData.itemName,
        quantity,
        unitPrice,
        totalAmount,
        supplier: formData.supplier,
        customer: formData.customer,
        orderDate: formData.orderDate,
        deliveryDate: formData.deliveryDate,
        status: formData.status,
        notes: formData.notes
      };
      setOrders([newOrder, ...orders]);
    }
    
    handleCloseModal();
  };

  const handleDelete = (orderId: string) => {
    if (window.confirm('この注文を削除してもよろしいですか？')) {
      setOrders(orders.filter(order => order.id !== orderId));
    }
  };

  const calculateTotal = () => {
    const quantity = parseInt(formData.quantity) || 0;
    const unitPrice = parseInt(formData.unitPrice) || 0;
    return quantity * unitPrice;
  };

  return (
    <div className="orders-management">
      <div className="inventory-header">
        <h2>受発注入力</h2>
        <button className="add-btn" onClick={() => handleOpenModal()}>
          <MdAdd /> 新規作成
        </button>
      </div>

      {/* フィルター・検索エリア */}
      <div className="order-filters">
        <div className="filter-group">
          <label>種別:</label>
          <select 
            className="filter-select"
            value={filterType}
            onChange={(e) => setFilterType(e.target.value as any)}
          >
            <option value="all">全て</option>
            <option value="受注">受注</option>
            <option value="発注">発注</option>
          </select>
        </div>
        
        <div className="filter-group">
          <label>ステータス:</label>
          <select 
            className="filter-select"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value as any)}
          >
            <option value="all">全て</option>
            <option value="未処理">未処理</option>
            <option value="処理中">処理中</option>
            <option value="完了">完了</option>
            <option value="キャンセル">キャンセル</option>
          </select>
        </div>

        <div className="search-box">
          <MdSearch className="search-icon" />
          <input
            type="text"
            placeholder="商品名・注文番号で検索..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      {/* 受発注テーブル */}
      <div className="order-table-container">
        <table className="order-table">
          <thead>
            <tr>
              <th>種別</th>
              <th>注文番号</th>
              <th>商品名</th>
              <th>顧客/仕入先</th>
              <th>数量</th>
              <th>単価</th>
              <th>合計金額</th>
              <th>注文日</th>
              <th>納期</th>
              <th>ステータス</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            {filteredOrders.map((order) => (
              <tr key={order.id}>
                <td>
                  <span className={`order-type-badge ${getTypeClass(order.type)}`}>
                    {order.type}
                  </span>
                </td>
                <td className="order-number">{order.orderNumber}</td>
                <td className="item-name">{order.itemName}</td>
                <td>{order.type === '受注' ? order.customer : order.supplier}</td>
                <td className="text-right">{order.quantity.toLocaleString()}</td>
                <td className="text-right">¥{order.unitPrice.toLocaleString()}</td>
                <td className="text-right total-amount">¥{order.totalAmount.toLocaleString()}</td>
                <td>{order.orderDate}</td>
                <td>{order.deliveryDate}</td>
                <td>
                  <span className={`status-badge ${getStatusClass(order.status)}`}>
                    {order.status}
                  </span>
                </td>
                <td className="action-buttons">
                  <button 
                    className="btn-action-icon btn-edit-icon" 
                    onClick={() => handleOpenModal(order)}
                    title="編集"
                  >
                    <MdEdit />
                  </button>
                  <button 
                    className="btn-action-icon btn-delete-icon" 
                    onClick={() => handleDelete(order.id)}
                    title="削除"
                  >
                    <MdDelete />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        
        {filteredOrders.length === 0 && (
          <div className="no-data">
            該当する受発注データがありません
          </div>
        )}
      </div>

      {/* 新規作成・編集モーダル */}
      {isModalOpen && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingOrder ? '受発注編集' : '新規受発注作成'}</h3>
              <button className="modal-close" onClick={handleCloseModal}>
                <MdClose />
              </button>
            </div>
            
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-row">
                  <div className="form-group">
                    <label>種別 <span className="required">*</span></label>
                    <select 
                      className="form-input"
                      value={formData.type}
                      onChange={(e) => setFormData({...formData, type: e.target.value as '受注' | '発注'})}
                      required
                    >
                      <option value="受注">受注</option>
                      <option value="発注">発注</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>ステータス <span className="required">*</span></label>
                    <select 
                      className="form-input"
                      value={formData.status}
                      onChange={(e) => setFormData({...formData, status: e.target.value as any})}
                      required
                    >
                      <option value="未処理">未処理</option>
                      <option value="処理中">処理中</option>
                      <option value="完了">完了</option>
                      <option value="キャンセル">キャンセル</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label>商品名 <span className="required">*</span></label>
                  <input 
                    type="text"
                    className="form-input"
                    value={formData.itemName}
                    onChange={(e) => setFormData({...formData, itemName: e.target.value})}
                    placeholder="商品名を入力"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>{formData.type === '受注' ? '顧客名' : '仕入先名'} <span className="required">*</span></label>
                  <input 
                    type="text"
                    className="form-input"
                    value={formData.type === '受注' ? formData.customer : formData.supplier}
                    onChange={(e) => setFormData({
                      ...formData, 
                      [formData.type === '受注' ? 'customer' : 'supplier']: e.target.value
                    })}
                    placeholder={formData.type === '受注' ? '顧客名を入力' : '仕入先名を入力'}
                    required
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>数量 <span className="required">*</span></label>
                    <input 
                      type="number"
                      className="form-input"
                      value={formData.quantity}
                      onChange={(e) => setFormData({...formData, quantity: e.target.value})}
                      placeholder="0"
                      min="1"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>単価 (¥) <span className="required">*</span></label>
                    <input 
                      type="number"
                      className="form-input"
                      value={formData.unitPrice}
                      onChange={(e) => setFormData({...formData, unitPrice: e.target.value})}
                      placeholder="0"
                      min="0"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>合計金額</label>
                    <div className="form-input-static">
                      ¥{calculateTotal().toLocaleString()}
                    </div>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>注文日 <span className="required">*</span></label>
                    <input 
                      type="date"
                      className="form-input"
                      value={formData.orderDate}
                      onChange={(e) => setFormData({...formData, orderDate: e.target.value})}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>納期 <span className="required">*</span></label>
                    <input 
                      type="date"
                      className="form-input"
                      value={formData.deliveryDate}
                      onChange={(e) => setFormData({...formData, deliveryDate: e.target.value})}
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>備考</label>
                  <textarea 
                    className="form-input form-textarea"
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    placeholder="備考を入力（任意）"
                    rows={3}
                  />
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn-cancel" onClick={handleCloseModal}>
                  キャンセル
                </button>
                <button type="submit" className="btn-submit">
                  {editingOrder ? '更新' : '作成'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

